import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Inventory2 as InventoryIcon } from '@mui/icons-material';
import { useInventoryStore } from '../../../stores/inventoryStore';
import { useTranslation } from '../../../i18n';
import {
  InventoryLocation,
  InventoryLocationCreate,
  InventoryLocationUpdate,
  InventoryItem,
  InventoryItemCreate,
  InventoryItemUpdate,
} from '../../../types/api';
import OperationsEquipmentSection from './OperationsEquipmentSection';
import {
  LocationTreePanel,
  LocationDialog,
  ItemDialog,
  InventoryItemsTable,
  buildLocationPath,
  collectAncestorIds,
} from './components/inventory';

export const InventoryTab: React.FC = () => {
  const { t } = useTranslation();
  const {
    locations,
    items,
    selectedLocationId,
    loading,
    error,
    loadLocations,
    loadItems,
    createLocation,
    updateLocation,
    deleteLocation: storeDeleteLocation,
    createItem,
    updateItem,
    deleteItem: storeDeleteItem,
    setSelectedLocationId,
  } = useInventoryStore();

  // UI state
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');
  const [locationSearch, setLocationSearch] = useState('');
  const [locationDialogOpen, setLocationDialogOpen] = useState(false);
  const [editingLocation, setEditingLocation] = useState<InventoryLocation | null>(null);
  const [itemDialogOpen, setItemDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<InventoryItem | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{
    type: 'location' | 'item';
    id: string;
    name: string;
  } | null>(null);

  const preSearchExpandedRef = React.useRef<Set<string> | null>(null);

  // Load data on mount
  useEffect(() => {
    loadLocations();
    loadItems();
  }, [loadLocations, loadItems]);

  // Reload items when selected location changes
  useEffect(() => {
    loadItems(selectedLocationId ?? undefined, search || undefined);
  }, [selectedLocationId, loadItems, search]);

  // Location search filtering
  const filteredLocationIds = useMemo(() => {
    if (!locationSearch.trim()) return null;
    const query = locationSearch.trim().toLowerCase();
    const matchIds = new Set(
      locations.filter((l) => l.name.toLowerCase().includes(query)).map((l) => l.id)
    );
    if (matchIds.size === 0) return new Set<string>();
    const ancestorIds = collectAncestorIds(matchIds, locations);
    return new Set([...matchIds, ...ancestorIds]);
  }, [locationSearch, locations]);

  // Auto-expand ancestors of search matches, restore on clear
  useEffect(() => {
    if (filteredLocationIds && filteredLocationIds.size > 0) {
      // Save pre-search state on first search expansion
      if (preSearchExpandedRef.current === null) {
        preSearchExpandedRef.current = new Set(expandedIds);
      }
      setExpandedIds((prev) => {
        const next = new Set(prev);
        for (const id of filteredLocationIds) {
          next.add(id);
        }
        return next;
      });
    } else if (filteredLocationIds === null && preSearchExpandedRef.current !== null) {
      // Search cleared — restore pre-search state
      setExpandedIds(preSearchExpandedRef.current);
      preSearchExpandedRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- expandedIds intentionally omitted: only snapshot once when search starts
  }, [filteredLocationIds]);

  // Tree helpers
  const rootLocations = useMemo(
    () =>
      locations
        .filter((l) => !l.parent_id && (!filteredLocationIds || filteredLocationIds.has(l.id)))
        .sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name)),
    [locations, filteredLocationIds]
  );

  const handleToggle = useCallback((id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const handleSelectLocation = useCallback(
    (id: string) => {
      setSelectedLocationId(selectedLocationId === id ? null : id);
    },
    [selectedLocationId, setSelectedLocationId]
  );

  // Location CRUD handlers
  const handleLocationSave = async (data: InventoryLocationCreate | InventoryLocationUpdate) => {
    if (editingLocation) {
      await updateLocation(editingLocation.id, data);
    } else {
      const created = await createLocation(data as InventoryLocationCreate);
      if (created && created.parent_id) {
        setExpandedIds((prev) => new Set(prev).add(created.parent_id as string));
      }
    }
    setLocationDialogOpen(false);
    setEditingLocation(null);
  };

  const handleEditLocation = (location: InventoryLocation) => {
    setEditingLocation(location);
    setLocationDialogOpen(true);
  };

  const handleDeleteLocationConfirm = (location: InventoryLocation) => {
    setDeleteConfirm({ type: 'location', id: location.id, name: location.name });
  };

  // Item CRUD handlers
  const handleItemSave = async (data: InventoryItemCreate | InventoryItemUpdate) => {
    if (editingItem) {
      await updateItem(editingItem.id, data);
    } else {
      await createItem(data as InventoryItemCreate);
    }
    setItemDialogOpen(false);
    setEditingItem(null);
  };

  const handleEditItem = (item: InventoryItem) => {
    setEditingItem(item);
    setItemDialogOpen(true);
  };

  const handleDeleteItemConfirm = (item: InventoryItem) => {
    setDeleteConfirm({ type: 'item', id: item.id, name: item.name });
  };

  const handleConfirmDelete = async () => {
    if (!deleteConfirm) return;
    if (deleteConfirm.type === 'location') {
      await storeDeleteLocation(deleteConfirm.id);
    } else {
      await storeDeleteItem(deleteConfirm.id);
    }
    setDeleteConfirm(null);
  };

  // Navigate to a location in the tree (expand ancestors + select)
  const navigateToLocation = useCallback(
    (locationId: string) => {
      const path = buildLocationPath(locationId, locations);
      setExpandedIds((prev) => {
        const next = new Set(prev);
        // Expand all ancestors (all except the last which is the target itself)
        for (let i = 0; i < path.length - 1; i++) {
          next.add(path[i].id);
        }
        return next;
      });
      setSelectedLocationId(locationId);
    },
    [locations, setSelectedLocationId]
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header */}
      <Box sx={{ mb: 2 }}>
        <Typography
          variant="h5"
          gutterBottom
          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
        >
          <InventoryIcon /> {t('inventory.title')}
        </Typography>
      </Box>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          onClose={() => useInventoryStore.setState({ error: null })}
        >
          {error}
        </Alert>
      )}

      {loading && !locations.length && !items.length ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ display: 'flex', gap: 2, flex: 1, minHeight: 0 }}>
          {/* Left panel - Location tree */}
          <LocationTreePanel
            locations={locations}
            rootLocations={rootLocations}
            selectedLocationId={selectedLocationId}
            expandedIds={expandedIds}
            filteredLocationIds={filteredLocationIds}
            locationSearch={locationSearch}
            onLocationSearchChange={setLocationSearch}
            onSelectLocation={handleSelectLocation}
            onSelectAllItems={() => setSelectedLocationId(null)}
            onToggle={handleToggle}
            onEditLocation={handleEditLocation}
            onDeleteLocation={handleDeleteLocationConfirm}
            onAddLocation={() => {
              setEditingLocation(null);
              setLocationDialogOpen(true);
            }}
            t={t}
          />

          {/* Right panel - Items table */}
          <InventoryItemsTable
            items={items}
            locations={locations}
            selectedLocationId={selectedLocationId}
            search={search}
            onSearchChange={setSearch}
            onAddItem={() => {
              setEditingItem(null);
              setItemDialogOpen(true);
            }}
            onEditItem={handleEditItem}
            onDeleteItem={handleDeleteItemConfirm}
            onNavigateToLocation={navigateToLocation}
            t={t}
          />
        </Box>
      )}

      {/* Location dialog */}
      <LocationDialog
        open={locationDialogOpen}
        onClose={() => {
          setLocationDialogOpen(false);
          setEditingLocation(null);
        }}
        onSave={handleLocationSave}
        locations={locations}
        editLocation={editingLocation}
        defaultParentId={selectedLocationId}
        t={t}
      />

      {/* Item dialog */}
      <ItemDialog
        open={itemDialogOpen}
        onClose={() => {
          setItemDialogOpen(false);
          setEditingItem(null);
        }}
        onSave={handleItemSave}
        locations={locations}
        editItem={editingItem}
        defaultLocationId={selectedLocationId}
        t={t}
      />

      {/* Delete confirmation */}
      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)}>
        <DialogTitle>{t('common.delete')}</DialogTitle>
        <DialogContent>
          <Typography>
            {deleteConfirm?.type === 'location'
              ? t('inventory.confirmDeleteLocation')
              : t('inventory.confirmDeleteItem')}
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, fontWeight: 600 }}>
            {deleteConfirm?.name}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)}>{t('common.cancel')}</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Operations Equipment — separate section below inventory */}
      <OperationsEquipmentSection />
    </Box>
  );
};
